import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Datas da API exibidas no horário de Brasília (2026-10-01).
//
// O backend grava em UTC e devolve SEM fuso (`isoformat()` de naive-UTC). Um
// `new Date("2026-08-16T00:45:00")` lê isso como horário LOCAL e mostra a data
// UTC: o que acontece entre 21h e 24h em Brasília aparece no dia seguinte.
//
// Regra:
// - com fuso (Z / ±hh:mm): convertido para Brasília;
// - sem fuso, com hora: é UTC → convertido para Brasília;
// - data pura ("2026-08-16") ou meia-noite exata sem fuso: é DIA DE CALENDÁRIO
//   (é assim que o backend grava datas como vencimento em colunas DateTime) e
//   fica como está — convertê-la mostraria o dia anterior.

const BRASILIA_TIME_ZONE = 'America/Sao_Paulo';
const HAS_TIMEZONE = /(Z|[+-]\d{2}:?\d{2})$/i;
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?)?$/;

export type ApiDateValue = string | number | Date | null | undefined;

const validOrNull = (date: Date): Date | null => (Number.isNaN(date.getTime()) ? null : date);

/** Instante real de um valor da API (sem fuso = UTC) — para comparar com "agora". */
export function parseApiInstant(value: ApiDateValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return validOrNull(value);
  if (typeof value === 'number') return validOrNull(new Date(value));
  const text = value.trim();
  const normalized = text.includes('T') && !HAS_TIMEZONE.test(text) ? `${text}Z` : text;
  return validOrNull(new Date(normalized));
}

const brasiliaParts = new Intl.DateTimeFormat('en-US', {
  timeZone: BRASILIA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Date cujos campos locais (dia, hora...) são os do relógio de Brasília —
 * pronta para `format` do date-fns. */
export function toBrasiliaWallClock(value: ApiDateValue): Date | null {
  if (typeof value === 'string') {
    const calendar = CALENDAR_DATE.exec(value.trim());
    if (calendar) {
      const [, year, month, day] = calendar.map(Number);
      return validOrNull(new Date(year, month - 1, day));
    }
  }
  const instant = parseApiInstant(value);
  if (!instant) return null;
  const parts = Object.fromEntries(
    brasiliaParts.formatToParts(instant).map((part) => [part.type, Number(part.value)]),
  );
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

/** Formata um valor da API no horário de Brasília com um padrão do date-fns
 * (pt-BR). Vazio ou inválido devolve `fallback`. */
export function formatBrasilia(value: ApiDateValue, pattern: string, fallback = '-'): string {
  const wallClock = toBrasiliaWallClock(value);
  return wallClock ? format(wallClock, pattern, { locale: ptBR }) : fallback;
}
